import {isBankUser, isUnitUser, useAccessToken, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {ErrorDocument} from "../../resources/common"
import {findDepositProducts} from "../../resources/depositProduct"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import React, {useEffect, useMemo} from "react"
import {useQueryState} from "use-location-state"
import SearchBox from "../../components/SearchBox/SearchBox"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {Filter} from "../Filter/Filter"
import {findOrgs, Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AllowedDepositProductsColumns} from "./DepositProducts.types"
import {DepositProductsTable} from "./DepositProductsTable"

interface DepositProductsProps {
	token?: string
	limit?: number
	includedColumns: AllowedDepositProductsColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	enableOrgFilter?: boolean
}

type StatusFilterOption = "true" | "false"

export default function DepositProducts({limit = 25, enableTitle, fullHeight, includedColumns}: DepositProductsProps) {
	const accessToken = useAccessToken()
	const userType = useUserType()
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>("filter[orgs]", [])
	const [query, setQuery] = useQueryState("filter[query]", "")
	const [enabledOnlyFilter, setEnabledOnlyFilter] = useQueryState<StatusFilterOption | "">("filter[enabledOnly]", "")
	const enabledOnlyBoolean = enabledOnlyFilter === "" ? undefined : enabledOnlyFilter === "true"
	const statusSelections: StatusFilterOption[] = enabledOnlyFilter === "" ? [] : [enabledOnlyFilter]
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) => findDepositProducts(accessToken, offset, limit, filteredOrgs, query, enabledOnlyBoolean),
		(x) => x.data.length,
		[filteredOrgs.join(","), query, enabledOnlyFilter ?? ""]
	)
	const orgs =
		isUnitUser(userType) || isBankUser(userType)
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()
	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const handleStatusChange = (next: StatusFilterOption[]) => {
		setEnabledOnlyFilter(next.length === 0 ? "" : next[next.length - 1])
		reset(limit)
	}

	const statusFilter = (
		<Filter
			title="Status"
			setStatuses={handleStatusChange}
			statuses={statusSelections}
			options={
				new Map<StatusFilterOption, string>([
					["true", "Active"],
					["false", "All"],
				])
			}
		/>
	)

	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Deposit Products", 500, onSearch)
	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	return (
		<DataTableCard className={"payments-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={searchBox}
				filters={[orgFilter, statusFilter]}
				title={enableTitle ? "Deposit Products" : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: depositProducts}) => {
					return (
						<DepositProductsTable
							depositProducts={depositProducts.data}
							meta={depositProducts.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={true}
							include={depositProducts.included}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
